import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Loading, Select } from "@aidkitorg/component-library";
import { usePost } from "../../API";
import type { Model as ScoreModel } from "@aidkitorg/aidkit/lib/convoai/schemas/Score";
import { ConversationReviewContext } from "./ConversationReviewPage";

const SCORE_OPTIONS = [0, 1];
const SCORE_LABELS = { 0: 'Fail', 1: 'Pass' };
const DEFAULT_SCORE_TYPES = ["Acceptable"];

export type ScoreRow = Partial<Pick<ScoreModel, 'id'>> & Pick<ScoreModel, 'score_type' | 'reasoning'> & { value: number | null, modified?: boolean };

type InlineScoringProps = {
  conversationId: string;
  messageIds: string[]; // IDs of the messages in this interaction
  scores: ScoreModel[],
  compact?: boolean; // For smaller inline display
};

export function InlineScoring({ conversationId, messageIds, compact = true }: InlineScoringProps) {
  const addScores = usePost('/coach/conversation/add_scores');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { updateScore, getScoresForMessageIds, readOnlyMode } = useContext(ConversationReviewContext);
  const scores = useMemo(() => getScoresForMessageIds(messageIds), [getScoresForMessageIds, messageIds]);
  const [scoreRows, setScoreRows] = useState<ScoreRow[]>(scores);
  
  useEffect(() => {
    const scoreRows: ScoreRow[] = scores.map(s => ({
      score_type: s.score_type,
      value: s.value,
      reasoning: s.reasoning
    }))
    for (const score_type of DEFAULT_SCORE_TYPES) {
      if (!scores.find(s => s.score_type === score_type)) { 
        scoreRows.unshift({
          score_type,
          reasoning: "",
          value: null
        });        
      }
    }
    setScoreRows(scoreRows);
  }, [scores])
  
  async function onSave() {
    setSaving(true);
    setError(null);
    setSuccess(false);
    
    const scoresToSave = scoreRows.filter(r => r.value !== null && r.value !== undefined);
    
    if (scoresToSave.length === 0) {
      setSaving(false);
      return;
    }
    
    try {
      const payload = {
        scores: scoresToSave.map(r => ({
          score_type: r.score_type,
          value: r.value!,
          reasoning: r.reasoning,
          conversation_id: conversationId,
          message_ids: messageIds.length === 0 ? null : messageIds // Use actual messageIds for interactions, null for overall
        }))
      };

      console.debug('🔍 Saving scores with payload:', {
        conversationId,
        messageIds,
        isOverallScoring: messageIds.length === 0,
        payload
      });

      const resp = await addScores(payload);
      
      if ('error' in resp) {
        setError(typeof resp.error === 'string' ? resp.error : 'Failed to save scores');
      } else {
        for (const result of resp.results ?? []) {
          updateScore(result.id, result);
        }
        setSuccess(true);
        setIsExpanded(false);        
        setSuccess(false);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save scores');
    } finally {
      setSaving(false);
    }
  }

  const hasScores = scores.some(r => r.value !== null);
    
  if (compact && !isExpanded) {
    return (
      <div className="border-t border-gray-200 pt-2 mt-2">
        <div className="flex items-center justify-between">
          <Button 
            isDisabled={readOnlyMode}
            title={readOnlyMode ? 'Conversation is not yet ingested, scoring unavailable' : 'Open the scoring menu for the conversation'}
            onClick={() => setIsExpanded(true)}
            variant={hasScores ? "secondary" : "primary"}
            size="sm"
          >
            {hasScores ? '✓ Scored' : (messageIds.length === 0 ? 'Score this conversation' : 'Score this interaction')}
          </Button>
          {hasScores && (
            <div className="flex space-x-1">
              {scores.filter(r => r.value !== null).map(r => (
                <span key={r.score_type} className={`text-xs px-1 py-0.5 rounded ${r.value === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {r.score_type}: {SCORE_LABELS[r.value as keyof typeof SCORE_LABELS]}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="border-t border-gray-200 pt-3 mt-3">
      {compact && (
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-900">
            {messageIds.length === 0 ? 'Rate this conversation' : 'Rate this interaction'}
          </h4>
          <button 
            onClick={() => setIsExpanded(false)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            ✕ Close
          </button>
        </div>
      )}
      
      {error && <p className="text-red-600 text-xs mb-2">{error}</p>}
      {success && <p className="text-green-700 text-xs mb-2">Scores saved.</p>}

      <div className="space-y-2">
        {scoreRows.map((row, index) => (
          <div key={row.score_type} className="flex items-start space-x-4">
            <div className="w-[125px] flex-shrink-0">
              <label className="text-xs font-medium text-gray-700 block mb-1">
                {row.score_type}
              </label>
              <Select
                label=""
                description=""
                hideLabel={true}
                className={`text-xs ${row.value === null ? 'border-red-300 bg-red-50' : ''}`}
                value={(() => {
                  const options = SCORE_OPTIONS.map(v => ({ value: v.toString(), label: SCORE_LABELS[v as keyof typeof SCORE_LABELS] }));
                  return options.find(opt => opt.value === (row.value?.toString() ?? ''));
                })()}
                onChange={(selectedOption) => {
                  setScoreRows(prev => {
                    const next = [...prev];
                    next[index] = { ...next[index], value: selectedOption?.value === '' ? null : Number(selectedOption?.value), modified: true };
                    return next;
                  });
                }}
                placeholder={"-- Select --"}
                options={SCORE_OPTIONS.map(v => ({ value: v.toString(), label: SCORE_LABELS[v as keyof typeof SCORE_LABELS] }))}
              />
            </div>
            <div className="flex-1 min-w-0">
              <label className="text-xs font-medium text-gray-700 block mb-1">
                Reasoning
              </label>
              <textarea
                className="w-full border rounded px-2 py-1 text-sm resize-y"
                rows={1}
                value={row.reasoning}
                onChange={(e) => {                  
                  setScoreRows(prev => {
                    const next = [...prev];
                    next[index] = { ...next[index], reasoning: e.target.value, modified: true };
                    return next;
                  });
                }}
                placeholder="Explain your reasoning for this score..."
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end mt-3">
        <Button 
          onClick={onSave} 
          isDisabled={saving || scoreRows.filter(r => r.modified && r.value !== null).length === 0}
          size="sm"
        >
          {saving ? <Loading loading={true} /> : 'Save Scores'}
        </Button>
      </div>
    </div>
  );
}
